<template>
    <div class="af-profile-details">
        <PageHeading>{{ $t('dashboard_menu.profile_details') }}</PageHeading>

        <Submenu
            :menu="submenu"
            @menuChange="changeTab"
        />

        <ProfileInfoForm
            :show-download-btn="showDownloadBtn"
            v-show="isBasicInfoActive"
        />

        <div v-show="isAdvancedInfoActive">
            <SeekerCustomAttributes :attribute-group="'info'" />
            <div
                v-if="showDownloadBtn"
                class="af-profile-pdf-link afu-text-center afu-mt-5 md:afu-mt-0"
            >
                <a
                    class="af-btn afu-cursor-pointer af-btn-primary afu-text-center afu-truncate afu-inline-flex afu-items-center afu-justify-center afu-border-btnWidth afu-border-solid focus:afu-outline-none afu-rounded-btn afu-transition afu-ease-in-out afu-duration-150 afu-text-neutral-light hover:afu-text-neutral-light focus:afu-text-neutral-light af-text--xl afu-font-semibold afu-border-primary hover:afu-border-primary-dark focus:afu-border-primary-dark afu-bg-primary active:afu-bg-primary-dark focus:afu-bg-primary-dark hover:afu-bg-primary-dark afu-p-btn afu-w-full"
                    style="box-sizing: border-box;"
                    @click.stop.prevent="downloadProfilePDF"
                >
                    Download profile as PDF
                </a>
            </div>
        </div>

        <SeekerAttributesTypeahead
            v-show="isSkillsActive"
            :attribute-id="10"
            :label="$_seekerFieldByCode('skills').label"
            :disabled="$_seekerFieldByCode('skills').read_only"
            attribute-code="skills"
            allow-new
            class="mb-0"
        />

        <WorkExperience v-show="isWorkExperienceActive" />

        <Education v-show="isEducationActive" />

        <Reference v-show="isReferenceActive" />

        <SeekerAttributesTypeahead
            v-show="isLicensesActive"
            :attribute-id="9"
            :label="$_seekerFieldByCode('license').label"
            :disabled="$_seekerFieldByCode('license').read_only"
            attribute-code="certifications"
            uppercase
            allow-new
            class="mb-0"
        />
    </div>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';
    import ProfileInfoForm from '@/apps/dashboard/001/components/Profile/ProfileInfoForm';
    import SeekerAttributesTypeahead from '@/apps/dashboard/001/components/SeekerAttributesTypeahead';
    import WorkExperience from '@/apps/dashboard/001/components/WorkExperience/WorkExperience';
    import Education from '@/apps/dashboard/001/components/Education/Education';
    import Reference from '@/apps/dashboard/001/components/Reference/Reference';
    import PageHeading from '@/apps/dashboard/001/components/PageHeading';
    import Submenu from '@/apps/dashboard/001/components/Submenu';
    import SeekerCustomAttributes from '@/apps/dashboard/001/components/SeekerCustomAttributes';
    import DashboardMixin from '@/apps/dashboard/mixins/DashboardMixin';

    export default {
        name: 'Profile',

        components: {
            Submenu,
            ProfileInfoForm,
            SeekerAttributesTypeahead,
            WorkExperience,
            Education,
            Reference,
            PageHeading,
            SeekerCustomAttributes,
        },

        mixins: [DashboardMixin],

        data() {
            return {
                submenu: [],
                spinner: {
                    update: false,
                },
                showDownloadBtn: false,
            };
        },

        created() {
            let basic_info = [];

            if (!this.$_seekerSectionByCode('basic_info').is_hidden) {
                basic_info = [
                    {
                        id: 'basic_info',
                        label: this.$t('label.basic_information'),
                        is_active: false,
                    },
                ];
            }

            let skills = [];

            if (!this.$_seekerSectionByCode('skills').is_hidden) {
                skills = [
                    {
                        id: 'skills',
                        label:
                            this.$_seekerFieldByCode('skills').label ||
                            this.$t('label.skills'),
                        is_active: false,
                    },
                ];
            }

            let reference = [];
            if (!this.$_seekerSectionByCode('reference').is_hidden) {
                reference = [
                    {
                        id: 'reference',
                        label:
                            this.$_seekerFieldByCode('reference').label ||
                            this.$t('label.reference'),
                        is_active: false,
                    },
                ];
            }

            let work_experience = [];
            if (!this.$_seekerSectionByCode('work_experience').is_hidden) {
                work_experience = [
                    {
                        id: 'work_experience',
                        label:
                            this.$_seekerFieldByCode('work_experience').label ||
                            this.$t('label.work_experience'),
                        is_active: false,
                    },
                ];
            }

            let education = [];
            if (!this.$_seekerSectionByCode('education').is_hidden) {
                education = [
                    {
                        id: 'education',
                        label:
                            this.$_seekerFieldByCode('education').label ||
                            this.$t('label.education'),
                        is_active: false,
                    },
                ];
            }

            let license = [];
            if (!this.$_seekerSectionByCode('license').is_hidden) {
                license = [
                    {
                        id: 'license',
                        label:
                            this.$_seekerFieldByCode('license').label ||
                            this.$t('label.licenses_and_certifications'),
                        is_active: false,
                    },
                ];
            }

            this.submenu = [
                ...basic_info,
                ...skills,
                ...reference,
                ...work_experience,
                ...education,
                ...license,
            ];

            if (this.submenu[0]) {
                this.submenu[0].is_active = true;
            }
            this.getCustomAttributes();
        },

        computed: {
            ...mapState({
                $_meAttributeOptions: state => state.me.attributeOptions,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
                $_seekerSectionByCode: 'seekerSectionByCode',
            }),

            isBasicInfoActive() {
                const menu = this.findMenuById('basic_info');

                return menu ? menu.is_active : false;
            },

            isSkillsActive() {
                const menu = this.findMenuById('skills');
                return menu ? menu.is_active : false;
            },

            isReferenceActive() {
                const menu = this.findMenuById('reference');
                return menu ? menu.is_active : false;
            },

            isWorkExperienceActive() {
                const menu = this.findMenuById('work_experience');
                return menu ? menu.is_active : false;
            },

            isEducationActive() {
                const menu = this.findMenuById('education');
                return menu ? menu.is_active : false;
            },

            isLicensesActive() {
                const menu = this.findMenuById('license');
                return menu ? menu.is_active : false;
            },

            isAdvancedInfoActive() {
                const menu = this.findMenuById('advanced_info');
                return menu ? menu.is_active : false;
            },
        },

        methods: {
            ...mapActions('me', {
                $_getMe: 'getMe',
            }),

            ...mapActions('me', {
                $_getCustomAttributes: 'getCustomAttributes',
            }),

            getCustomAttributes() {
                const group = 'info';
                this.$_getCustomAttributes({ group }).then(res => {
                    if (
                        res.data.length > 0 &&
                        !this.$_seekerSectionByCode('basic_info').is_hidden
                    ) {
                        this.submenu.splice(1, 0, {
                            id: 'advanced_info',
                            label: 'Advanced information',
                            is_active: false,
                        });

                        res.data.forEach(cf => {
                            if (
                                cf.title === 'Video introduction 2024' &&
                                cf.value !== null &&
                                cf.value !== ''
                            ) {
                                this.showDownloadBtn = true;
                            }
                        });
                    }
                });
            },

            findMenuById(id) {
                return this.submenu.find(o => o.id === id);
            },

            changeTab(nav) {
                this.submenu.forEach(o => (o.is_active = nav.id === o.id));
            },
        },
    };
</script>
